import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Loader2, Shield, UserPlus } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatAppRoles, formatLastLogin, isAccountPaused } from "@/config/adminAccountOptions";
import { buildToolAccessMatrix, PERSONA_COLUMNS } from "@/config/toolAccessMatrix";
import { useAuth } from "@/hooks/useAuth";
import { PERSONA_LABELS } from "@/config/workspacePersona";
import {
  createAdminUser,
  type AdminUserPersona,
} from "@/services/adminCreateUserService";
import {
  grantPlatformAdminByEmail,
  listAllAccounts,
  listLicenseInventory,
  listPlatformAdmins,
  setPlatformAdmin,
  upsertOrgLicense,
} from "@/services/systemAdminService";
import {
  applyDistrictFilter,
  buildAccountFilterOptions,
  describeActiveFilters,
  describeSort,
  EMPTY_ACCOUNT_FILTERS,
  filterAccounts,
  nextSortState,
  sortAccounts,
  type AccountFilterKey,
  type AccountFilters,
  type AccountSort,
  type AccountSortColumn,
} from "@/lib/accountListView";
import type { AdminAccountRow, LicenseInventoryRow, PlatformAdminRow } from "@/types/systemAdmin";
import { AccountActionsCell } from "./AccountActionsCell";
import { AccountsFilterBar } from "./AccountsFilterBar";
import { AddDistrictDialog } from "./AddDistrictDialog";
import { AddSchoolDialog } from "./AddSchoolDialog";
import { EditDistrictDialog } from "./EditDistrictDialog";
import { EditSchoolDialog } from "./EditSchoolDialog";
import { SortableHeader } from "./SortableHeader";

// Radix Select rejects an item with value="", so "no school" needs a real
// sentinel value instead of the empty string newUserSchoolId uses internally.
const NO_SCHOOL_OPTION = "none";

export const SystemAdminPage = () => {
  const { toast } = useToast();
  const { session } = useAuth();
  const currentUserId = session?.user.id;
  const toolMatrix = useMemo(() => buildToolAccessMatrix(), []);

  const [accounts, setAccounts] = useState<AdminAccountRow[]>([]);
  const [licenses, setLicenses] = useState<LicenseInventoryRow[]>([]);
  const [admins, setAdmins] = useState<PlatformAdminRow[]>([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountSort, setAccountSort] = useState<AccountSort | null>(null);
  const [accountFilters, setAccountFilters] = useState<AccountFilters>(EMPTY_ACCOUNT_FILTERS);
  const [adminEmail, setAdminEmail] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserDistrictId, setNewUserDistrictId] = useState("");
  const [newUserSchoolId, setNewUserSchoolId] = useState("");
  const [newUserPersona, setNewUserPersona] = useState<AdminUserPersona>("teacher");
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Lifted out of Tabs itself: refresh() toggles isLoading around every
  // save action, which swaps Tabs for the spinner below and back, unmounting
  // and remounting it. An uncontrolled Tabs would forget which tab was
  // active on that remount and reopen on its defaultValue every time.
  const [activeTab, setActiveTab] = useState("users");

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const [nextAccounts, nextLicenses, nextAdmins] = await Promise.all([
        listAllAccounts(),
        listLicenseInventory(),
        listPlatformAdmins(),
      ]);
      setAccounts(nextAccounts);
      setLicenses(nextLicenses);
      setAdmins(nextAdmins);
    } catch (err) {
      toast({
        title: "Could not load admin data",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const accountFilterOptions = useMemo(
    () => buildAccountFilterOptions(accounts, accountFilters),
    [accounts, accountFilters]
  );

  const visibleAccounts = useMemo(
    () => sortAccounts(filterAccounts(accounts, accountFilters, accountSearch), accountSort),
    [accounts, accountFilters, accountSearch, accountSort]
  );

  const activeAccountFilters = useMemo(
    () => describeActiveFilters(accountFilters, accountFilterOptions),
    [accountFilters, accountFilterOptions]
  );

  const handleSort = useCallback((column: AccountSortColumn) => {
    setAccountSort((current) => nextSortState(current, column));
  }, []);

  const handleAccountFilterChange = useCallback(
    (key: AccountFilterKey, value: string) => {
      setAccountFilters((current) =>
        key === "district"
          ? applyDistrictFilter(current, value, accounts)
          : { ...current, [key]: value }
      );
    },
    [accounts]
  );

  const handleClearAccountView = useCallback(() => {
    setAccountFilters(EMPTY_ACCOUNT_FILTERS);
    setAccountSort(null);
    setAccountSearch("");
  }, []);

  const districtRows = useMemo(
    () => licenses.filter((row) => row.scopeKind === "district"),
    [licenses]
  );

  const schoolsByDistrict = useMemo(() => {
    const map = new Map<string, LicenseInventoryRow[]>();
    for (const row of licenses) {
      if (row.scopeKind !== "school" || !row.schoolId) continue;
      const list = map.get(row.districtId) ?? [];
      list.push(row);
      map.set(row.districtId, list);
    }
    return map;
  }, [licenses]);

  const saveLicense = async (
    row: LicenseInventoryRow,
    leaderTotal: number,
    staffTotal: number
  ) => {
    const key = row.schoolId ?? row.districtId;
    setBusyKey(key);
    try {
      await upsertOrgLicense({
        districtId: row.scopeKind === "district" ? row.districtId : null,
        schoolId: row.schoolId,
        leaderTotal,
        staffTotal,
      });
      toast({ title: "Seat pools updated" });
      await refresh();
    } catch (err) {
      toast({
        title: "Update failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleGrantAdmin = async () => {
    setBusyKey("grant-admin");
    try {
      await grantPlatformAdminByEmail(adminEmail);
      setAdminEmail("");
      toast({ title: "Platform admin added" });
      await refresh();
    } catch (err) {
      toast({
        title: "Could not add admin",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleRevokeAdmin = async (userId: string) => {
    setBusyKey(`revoke-${userId}`);
    try {
      await setPlatformAdmin(userId, false);
      toast({ title: "Platform admin removed" });
      await refresh();
    } catch (err) {
      toast({
        title: "Could not remove admin",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyKey(null);
    }
  };

  /**
   * Create an account and tie it to a district, optionally to a school in it.
   *
   * Every rule lives server-side in the admin-create-user function — the
   * district email-domain match (including final-plus routing), seat
   * availability, and the write order that makes the account appear in the list
   * above. So a refused create is shown with the server's own wording, which
   * names the domains involved; a generic message would drop the only
   * actionable part.
   */
  const handleAddUser = async () => {
    if (!newUserEmail.trim() || !newUserDistrictId) return;
    setBusyKey("create-user");
    try {
      const result = await createAdminUser({
        email: newUserEmail,
        districtId: newUserDistrictId,
        schoolId: newUserSchoolId || null,
        persona: newUserPersona,
        firstName: newUserFirstName.trim() || null,
        lastName: newUserLastName.trim() || null,
      });

      // `result.ok === false`, not `!result.ok`: this project compiles with
      // strict: false, and without strictNullChecks TypeScript will not narrow a
      // discriminated union through a negation. The explicit comparison does
      // narrow. Simplifying it back reintroduces a tsc error here.
      if (result.ok === false) {
        toast({
          title: "Could not add user",
          description: result.errorMessage,
          variant: "destructive",
        });
        return;
      }

      setNewUserEmail("");
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserDistrictId("");
      setNewUserSchoolId("");
      setNewUserPersona("teacher");
      setIsAddUserOpen(false);
      toast({
        title: "User added",
        description: `${result.user.email} can sign in from the email just sent.`,
      });
      await refresh();
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Page.Container>
      <Page.Header />
      <Page.Content className="space-y-4">
        <div className="flex items-start gap-3 text-white">
          <Shield className="h-8 w-8 shrink-0 text-bodhi-orange" aria-hidden />
          <div>
            <h1 className="text-2xl font-semibold">Platform administration</h1>
            <p className="text-sm text-white/75 mt-1 max-w-2xl">
              Manage accounts, seat pools for districts and schools, platform admins, tool
              access by persona, and per-user roles or pauses.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white/10 text-white flex flex-wrap h-auto gap-1">
              <TabsTrigger value="users">Accounts</TabsTrigger>
              <TabsTrigger value="licenses">Seat pools</TabsTrigger>
              <TabsTrigger value="tools">Tool access</TabsTrigger>
              <TabsTrigger value="admins">Platform admins</TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="mt-4 space-y-4">
              <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add user</DialogTitle>
                    <DialogDescription>
                      The address must match the district&apos;s email domain. Leave the
                      school unset for a district-level account. A sign-in email goes out
                      immediately.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-1">
                        <label htmlFor="new-user-email" className="text-sm text-gray-600">
                          Email
                        </label>
                        <span aria-hidden className="text-sm text-destructive">
                          *
                        </span>
                      </div>
                      <Input
                        id="new-user-email"
                        type="email"
                        required
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="teacher@d105.net"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="new-user-persona" className="text-sm text-gray-600">
                        Persona
                      </label>
                      <Select
                        value={newUserPersona}
                        onValueChange={(value) => setNewUserPersona(value as AdminUserPersona)}
                      >
                        <SelectTrigger id="new-user-persona">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            [
                              "teacher",
                              "support_staff",
                              "leader",
                              "district_leader",
                            ] as AdminUserPersona[]
                          ).map((persona) => (
                            <SelectItem key={persona} value={persona}>
                              {PERSONA_LABELS[persona]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="new-user-first-name" className="text-sm text-gray-600">
                        First name (optional)
                      </label>
                      <Input
                        id="new-user-first-name"
                        value={newUserFirstName}
                        onChange={(e) => setNewUserFirstName(e.target.value)}
                        placeholder="Dana"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="new-user-last-name" className="text-sm text-gray-600">
                        Last name (optional)
                      </label>
                      <Input
                        id="new-user-last-name"
                        value={newUserLastName}
                        onChange={(e) => setNewUserLastName(e.target.value)}
                        placeholder="Reed"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-1">
                        <label htmlFor="new-user-district" className="text-sm text-gray-600">
                          District
                        </label>
                        <span aria-hidden className="text-sm text-destructive">
                          *
                        </span>
                      </div>
                      <Select
                        required
                        value={newUserDistrictId}
                        onValueChange={(value) => {
                          setNewUserDistrictId(value);
                          // A school from the previous district would be rejected
                          // server-side, so it is cleared rather than carried over.
                          setNewUserSchoolId("");
                        }}
                      >
                        <SelectTrigger id="new-user-district">
                          <SelectValue placeholder="Select district…" />
                        </SelectTrigger>
                        <SelectContent>
                          {districtRows.map((d) => (
                            <SelectItem key={d.districtId} value={d.districtId}>
                              {d.districtName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="new-user-school" className="text-sm text-gray-600">
                        School (optional)
                      </label>
                      <Select
                        value={newUserSchoolId || NO_SCHOOL_OPTION}
                        onValueChange={(value) =>
                          setNewUserSchoolId(value === NO_SCHOOL_OPTION ? "" : value)
                        }
                        disabled={!newUserDistrictId}
                      >
                        <SelectTrigger id="new-user-school">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_SCHOOL_OPTION}>
                            No school — district level
                          </SelectItem>
                          {(schoolsByDistrict.get(newUserDistrictId) ?? []).map((school) => (
                            <SelectItem key={school.schoolId} value={school.schoolId ?? ""}>
                              {school.schoolName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      disabled={
                        busyKey === "create-user" || !newUserEmail.trim() || !newUserDistrictId
                      }
                      onClick={() => void handleAddUser()}
                    >
                      {busyKey === "create-user" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          Adding…
                        </>
                      ) : (
                        "Add user"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <ContentCard>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
                  <p className="text-sm text-gray-600">
                    {visibleAccounts.length} account{visibleAccounts.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:flex-1 sm:justify-end">
                    <Input
                      placeholder="Search name, email, district, school…"
                      value={accountSearch}
                      onChange={(e) => setAccountSearch(e.target.value)}
                      className="w-full flex-1 max-w-md bg-white"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        // Open fresh: carrying over an abandoned attempt's
                        // email, name, district or persona would be
                        // surprising, not helpful.
                        setNewUserEmail("");
                        setNewUserFirstName("");
                        setNewUserLastName("");
                        setNewUserDistrictId("");
                        setNewUserSchoolId("");
                        setNewUserPersona("teacher");
                        setIsAddUserOpen(true);
                      }}
                    >
                      <UserPlus className="h-4 w-4" aria-hidden />
                      Add user
                    </Button>
                  </div>
                </div>
                <AccountsFilterBar
                  filters={accountFilters}
                  options={accountFilterOptions}
                  chips={activeAccountFilters}
                  sortDescription={describeSort(accountSort)}
                  onFilterChange={handleAccountFilterChange}
                  onClearAll={handleClearAccountView}
                />
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHeader
                          column="name"
                          label="Name"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="email"
                          label="Email"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="org"
                          label="Org"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="orgRole"
                          label="Org role"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="status"
                          label="Status"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="lastLogin"
                          label="Last login"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="admin"
                          label="Admin"
                          sort={accountSort}
                          onSort={handleSort}
                        />
                        <TableHead>Manage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleAccounts.map((row) => (
                        <TableRow
                          key={row.userId}
                          className={isAccountPaused(row.userStatus) ? "bg-gray-50/80" : undefined}
                        >
                          <TableCell className="font-medium">{row.userName}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell className="text-xs text-gray-600">
                            {[row.districtName, row.schoolName].filter(Boolean).join(" · ") ||
                              "—"}
                          </TableCell>
                          <TableCell className="text-xs">{formatAppRoles(row.roles)}</TableCell>
                          <TableCell>
                            <span
                              className={
                                isAccountPaused(row.userStatus)
                                  ? "text-amber-700 font-medium"
                                  : undefined
                              }
                            >
                              {isAccountPaused(row.userStatus) ? "Paused" : row.userStatus}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                            {formatLastLogin(row.lastSignInAt)}
                          </TableCell>
                          <TableCell>{row.isSuperAdmin ? "Yes" : "—"}</TableCell>
                          <TableCell>
                            <AccountActionsCell
                              account={row}
                              currentUserId={currentUserId}
                              onUpdated={refresh}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Kept outside the table: a colSpan row would let the browser
                      recompute column widths from the header alone, so the columns
                      visibly jump the moment a filter empties the list. */}
                  {visibleAccounts.length === 0 ? (
                    <p className="text-sm text-gray-600 text-center py-6">
                      {accounts.length === 0
                        ? "No accounts yet."
                        : "No accounts match the current filters."}
                    </p>
                  ) : null}
                </div>
              </ContentCard>
            </TabsContent>

            <TabsContent value="licenses" className="mt-4 space-y-4">
              <ContentCard>
                <h2 className="font-semibold text-gray-900 mb-3">Add organization</h2>
                <div className="flex flex-wrap gap-2">
                  <AddDistrictDialog onCreated={refresh} />
                  <AddSchoolDialog districtRows={districtRows} onCreated={refresh} />
                </div>
              </ContentCard>

              {districtRows.map((district) => (
                <ContentCard key={district.districtId}>
                  <LicenseEditor
                    title={`${district.districtName} (district-wide)`}
                    titleAction={<EditDistrictDialog district={district} onUpdated={refresh} />}
                    row={district}
                    busy={busyKey === district.districtId}
                    onSave={saveLicense}
                  />
                  <div className="mt-3">
                    <AddSchoolDialog
                      districtId={district.districtId}
                      districtName={district.districtName}
                      onCreated={refresh}
                    />
                  </div>
                  {(schoolsByDistrict.get(district.districtId) ?? []).map((school) => (
                    <div key={school.schoolId} className="mt-4 pt-4 border-t border-gray-100">
                      <LicenseEditor
                        title={school.schoolName ?? "School"}
                        titleAction={<EditSchoolDialog school={school} onUpdated={refresh} />}
                        row={school}
                        busy={busyKey === (school.schoolId ?? "")}
                        onSave={saveLicense}
                      />
                    </div>
                  ))}
                </ContentCard>
              ))}
            </TabsContent>

            <TabsContent value="tools" className="mt-4">
              <ContentCard>
                <p className="text-sm text-gray-600 mb-4">
                  Based on sidebar navigation rules in{" "}
                  <code className="text-xs bg-gray-100 px-1 rounded">workspacePersona.ts</code>.
                  Leaders assign personas when inviting teammates; district leaders share the
                  leader tool set.
                </p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Section</TableHead>
                        <TableHead>Tool</TableHead>
                        {PERSONA_COLUMNS.map((col) => (
                          <TableHead key={col.id} className="text-center min-w-[5rem]">
                            {col.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {toolMatrix.map((tool) => (
                        <TableRow key={tool.toolId}>
                          <TableCell className="text-gray-500">{tool.section}</TableCell>
                          <TableCell>
                            <div className="font-medium">{tool.label}</div>
                            <div className="text-xs text-gray-400">{tool.pathOrEntry}</div>
                          </TableCell>
                          {PERSONA_COLUMNS.map((col) => (
                            <TableCell key={col.id} className="text-center">
                              {tool.accessByPersona[col.id] ? "✓" : "—"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ContentCard>
            </TabsContent>

            <TabsContent value="admins" className="mt-4 space-y-4">
              <ContentCard>
                <h2 className="font-semibold text-gray-900 mb-2">Add platform admin</h2>
                <p className="text-sm text-gray-600 mb-3">
                  They can open this panel, view all accounts, and adjust seat pools. The
                  account must already exist in Hey Bodhi.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
                  <Input
                    type="email"
                    placeholder="colleague@district.org"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                  />
                  <Button
                    type="button"
                    disabled={busyKey === "grant-admin" || !adminEmail.trim()}
                    onClick={() => void handleGrantAdmin()}
                  >
                    Grant access
                  </Button>
                </div>
              </ContentCard>

              <ContentCard>
                <h2 className="font-semibold text-gray-900 mb-3">Current platform admins</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {admins.map((admin) => (
                      <TableRow key={admin.userId}>
                        <TableCell>{admin.userName}</TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={busyKey === `revoke-${admin.userId}`}
                            onClick={() => void handleRevokeAdmin(admin.userId)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ContentCard>
            </TabsContent>
          </Tabs>
        )}
      </Page.Content>
    </Page.Container>
  );
};

function LicenseEditor({
  title,
  titleAction,
  row,
  busy,
  onSave,
}: {
  title: string;
  titleAction?: ReactNode;
  row: LicenseInventoryRow;
  busy: boolean;
  onSave: (row: LicenseInventoryRow, leaderTotal: number, staffTotal: number) => Promise<void>;
}) {
  const [leaderTotal, setLeaderTotal] = useState(String(row.leaderTotal));
  const [staffTotal, setStaffTotal] = useState(String(row.staffTotal));

  useEffect(() => {
    setLeaderTotal(String(row.leaderTotal));
    setStaffTotal(String(row.staffTotal));
  }, [row.leaderTotal, row.staffTotal]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium text-gray-900">{title}</h3>
        {titleAction}
      </div>
      <p className="text-xs text-gray-500 mt-1 mb-3">
        Leader seats: {row.leaderUsed} used · Staff seats: {row.staffUsed} used
      </p>
      <div className="grid gap-3 sm:grid-cols-3 sm:items-end max-w-xl">
        <label className="text-sm">
          <span className="text-gray-600 block mb-1">Leader total</span>
          <Input
            type="number"
            min={0}
            value={leaderTotal}
            onChange={(e) => setLeaderTotal(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="text-gray-600 block mb-1">Staff total</span>
          <Input
            type="number"
            min={0}
            value={staffTotal}
            onChange={(e) => setStaffTotal(e.target.value)}
          />
        </label>
        <Button
          type="button"
          disabled={busy}
          onClick={() =>
            void onSave(row, Number(leaderTotal) || 0, Number(staffTotal) || 0)
          }
        >
          Save pools
        </Button>
      </div>
    </div>
  );
}
